/**
 * The two cards of the E-Mail-Absender page, Absender and Testversand, with the
 * one save bar both share (T1b). ONE FORM: the four fields are one settings
 * object on the server, and one save sends whichever of them changed.
 *
 * `noValidate`: the browser's own e-mail check would answer in its own words
 * and before the page's; the page checks the obvious first and the server
 * decides the rest, both shown at the field.
 *
 * THE SAVE BUTTON KEEPS ITS FOCUS. It is marked unavailable with
 * `aria-disabled` while nothing changed or a save runs, never with `disabled`:
 * a keyboard user who presses it must still be on it when the save is done.
 *
 * THE TEST ADDRESS IS CONFIGURATION ONLY. This page sends nothing — no test
 * mail, no button for one. The card says where test mails will go and that an
 * empty field means none can be sent; its status line reads the STORED value,
 * not the draft, because the draft has not been saved anywhere yet.
 */
import React from "react";

import EmailSettingsField from "./EmailSettingsField";
import { FIELD_LABELS, MAX_LENGTH, canonical } from "./emailSettingsModel";

const CARD =
  "flex min-w-0 flex-col gap-4 rounded-[14px] border border-border bg-card p-4 md:p-5";
const CARD_TITLE = "m-0 text-[14px] font-semibold text-foreground";
const CARD_TEXT = "m-0 mt-1 text-[12.5px] leading-snug text-muted-foreground";
const NOTE =
  "m-0 rounded-[10px] border border-border bg-background px-3 py-2.5 text-[12.5px] leading-snug text-foreground";
const ALERT =
  "m-0 rounded-[10px] border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] leading-snug text-amber-800 " +
  "dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300";
const SAVE =
  "inline-flex h-9 shrink-0 items-center justify-center rounded-[9px] bg-primary px-4 text-[13px] font-semibold " +
  "text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-card aria-disabled:cursor-not-allowed aria-disabled:opacity-60";

export const FIELD_IDS = {
  sender_name: "email-settings-sender-name",
  sender_email: "email-settings-sender-email",
  reply_to: "email-settings-reply-to",
  test_recipient_email: "email-settings-test-recipient",
};

function saveStatusText({ saving, dirty }) {
  if (saving) return "Änderungen werden gespeichert …";
  if (dirty) return "Ungespeicherte Änderungen";
  return "Keine ungespeicherten Änderungen";
}

export default function EmailSettingsForm({
  form,
  settings,
  fieldErrors,
  saving,
  dirty,
  saveError,
  onChange,
  onSubmit,
}) {
  const field = (name, props) => (
    <EmailSettingsField
      id={FIELD_IDS[name]}
      label={FIELD_LABELS[name]}
      value={form[name]}
      error={fieldErrors[name] || ""}
      maxLength={MAX_LENGTH[name]}
      readOnly={saving}
      onChange={(value) => onChange(name, value)}
      {...props}
    />
  );

  // From the STORED values the sender resolver needs (T1a), not from whether a
  // settings row exists: a row holding only a test address, or a cleared row,
  // is as unusable for sending as no row at all.
  const storedName = canonical(settings?.sender_name);
  const storedEmail = canonical(settings?.sender_email);
  let senderNote = null;
  if (!storedName && !storedEmail) {
    senderNote = "Für diese Organisation ist noch kein E-Mail-Absender eingerichtet.";
  } else if (!storedName || !storedEmail) {
    senderNote =
      "Der E-Mail-Absender ist unvollständig: Absendername und Absender-E-Mail werden beide benötigt.";
  }
  const storedTestRecipient = settings?.test_recipient_email || "";

  return (
    <form
      noValidate
      onSubmit={onSubmit}
      aria-label="E-Mail-Einstellungen"
      className="flex min-w-0 flex-col gap-4"
    >
      <section aria-labelledby="email-settings-sender-title" className={CARD}>
        <div>
          <h2 id="email-settings-sender-title" className={CARD_TITLE}>
            Absender
          </h2>
          <p className={CARD_TEXT}>
            So erscheinen automatisierte E-Mails dieser Organisation bei den
            Empfängern.
          </p>
        </div>
        {senderNote ? (
          <p className={NOTE} data-testid="email-settings-sender-status">
            {senderNote}
          </p>
        ) : null}
        <div className="grid grid-cols-1 gap-4">
          {field("sender_name", {
            hint: "Der Name, den Empfänger als Absender sehen.",
          })}
          {field("sender_email", {
            type: "email",
            hint: "Die Adresse, von der automatisierte E-Mails versendet werden.",
          })}
          {field("reply_to", {
            type: "email",
            optional: true,
            hint: "Antworten der Empfänger gehen an diese Adresse.",
          })}
        </div>
      </section>

      <section aria-labelledby="email-settings-test-title" className={CARD}>
        <div>
          <h2 id="email-settings-test-title" className={CARD_TITLE}>
            Testversand
          </h2>
          <p className={CARD_TEXT}>
            Testversände von E-Mail-Automationen werden ausschliesslich an diese
            Adresse gesendet.
          </p>
        </div>
        {field("test_recipient_email", {
          type: "email",
          hint:
            "Genau eine Adresse, gültig für die ganze Organisation. Leer lassen, " +
            "wenn kein Testversand möglich sein soll.",
        })}
        <p className={NOTE} data-testid="email-settings-test-status">
          {storedTestRecipient ? (
            <>
              Gespeichert:{" "}
              <span className="break-all font-medium [overflow-wrap:anywhere]">
                {storedTestRecipient}
              </span>
            </>
          ) : (
            "Keine Test-E-Mail-Adresse gespeichert. Ohne sie ist kein Testversand möglich."
          )}
        </p>
        <p className="m-0 text-[11.5px] leading-snug text-muted-foreground">
          Diese Seite versendet keine E-Mails. Hier wird nur die Adresse hinterlegt.
        </p>
      </section>

      {saveError ? (
        <p role="alert" className={ALERT}>
          {saveError.message}
        </p>
      ) : null}

      <div className="flex flex-col gap-3 rounded-[14px] border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
        <p
          role="status"
          aria-live="polite"
          className="m-0 text-[12.5px] text-muted-foreground"
        >
          {saveStatusText({ saving, dirty })}
        </p>
        {/* `aria-disabled`, not `disabled`: the button is pressed and then
            becomes unavailable while saving and once the form is clean again,
            and a natively disabled button would drop the focus to the page.
            Pressing it then does nothing — the save itself refuses a clean
            form and a second click. */}
        <button
          type="submit"
          aria-disabled={!dirty || saving ? "true" : undefined}
          aria-busy={saving ? "true" : undefined}
          className={SAVE}
        >
          {saving ? "Speichert …" : "Änderungen speichern"}
        </button>
      </div>
    </form>
  );
}
